/**
 * "Is somebody in the middle of writing a chat message?"
 *
 * The chat page rebuilds the open conversation whenever the browser tab comes
 * back into view, to pick up anything missed while it was hidden. Rebuilding
 * also rebuilds the message box, and on a phone "coming back into view" is
 * exactly what happens after the camera: tap Photos & videos, take a picture,
 * tap Use, and the browser returns to a brand new message box while the
 * picture is handed to the old one. Nothing was attached and nothing said why.
 * A half-typed message went the same way on every trip to another app.
 *
 * So the message box says here when it holds something unsent, and the attach
 * menu says when a file chooser (or the camera behind it) is open. The chat
 * page asks before rebuilding, and catches up once the box is empty again.
 *
 * Plain module state rather than React context: the two ends are a page and a
 * menu several components apart, and neither needs to re-render on a change.
 */

// A chooser left open and forgotten must not block catching up forever. Not
// every browser says when a chooser is dismissed without picking anything.
const PICK_TIMEOUT_MS = 5 * 60 * 1000;

// The chooser hands its files over a moment before the message box has them
// on show; it keeps counting as open for that long so nothing slips between.
const HANDOVER_MS = 400;

let pickingSince = 0;
let handover = null;
const drafts = new Set();
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener());

export const beginFilePick = () => {
    clearTimeout(handover);
    handover = null;
    pickingSince = Date.now();
};

export const endFilePick = () => {
    if (!pickingSince || handover) return;
    handover = setTimeout(() => {
        handover = null;
        pickingSince = 0;
        notify();
    }, HANDOVER_MS);
};

export const isPickingFile = () => pickingSince > 0 && Date.now() - pickingSince < PICK_TIMEOUT_MS;

/** The message box of `conversationId` holds (or no longer holds) something unsent. */
export const setUnsentDraft = (conversationId, hasDraft) => {
    const id = String(conversationId || '');
    if (!id || drafts.has(id) === Boolean(hasDraft)) return;
    if (hasDraft) drafts.add(id);
    else drafts.delete(id);
    notify();
};

export const hasUnsentDraft = (conversationId) => drafts.has(String(conversationId || ''));

/** True while rebuilding that conversation's message box would lose something. */
export const isComposing = (conversationId) => isPickingFile() || hasUnsentDraft(conversationId);

/** Called on every change, so a postponed catch-up can run. Returns the unsubscribe. */
export const onComposingChange = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};
